/**
 * Firestore Timestamp / ISO 文字列 / Date を相互変換するヘルパ。
 */
import { Timestamp } from 'firebase/firestore';

export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && value !== null) {
    // Firestore SDK の Timestamp（toDate メソッド付き）
    if ('toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
      try {
        return (value as { toDate(): Date }).toDate();
      } catch {
        return null;
      }
    }
    // Callable Function 経由の Timestamp は { _seconds, _nanoseconds } または { seconds, nanoseconds } に
    // シリアライズされる（toDate メソッドが失われる）
    const v = value as {
      _seconds?: number;
      _nanoseconds?: number;
      seconds?: number;
      nanoseconds?: number;
    };
    const seconds = v._seconds ?? v.seconds;
    const nanoseconds = v._nanoseconds ?? v.nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000 + Math.floor(nanoseconds / 1_000_000));
    }
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleString('ja-JP', { hour12: false }) : '—';
}

export function formatDate(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString('ja-JP') : '—';
}

/**
 * `YYYYMMDD`（8桁・ゼロ埋め）形式に整形する。
 * 主に PDF/印刷時のファイル名生成に使う。変換できない場合は空文字を返す。
 */
export function formatYmd(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

export function toTimestampOrNull(value: Date | string | null | undefined): Timestamp | null {
  if (!value) return null;
  if (value instanceof Date) return Timestamp.fromDate(value);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}

/** `<input type="datetime-local">` 用の値に変換 */
export function toDatetimeLocalValue(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
